import { useEffect, useState } from "react";
import {
  DialogActions,
  DialogContent,
  DialogTitle,
  Modal,
  Input,
  Select,
  Flex,
  Checkbox,
  Text,
  Icon,
  Button,
  Divider
} from "@theorgchartdev/storybook";
import cx from "classnames";
import CentralizedModalContainer from "../../../../../Utils/CentralizedModalContainer/CentralizedModalContainer";
import { GetWSMgr, UploadToSystemFolder } from "@/Utils/StateLib";
import {
  MetadataTypeOptions,
  metaDataTypes,
  NameIDTypeOptions,
  commonSelectProps,
  BuildLocalMetadataOptions,
  BuildAccessGroupOptions,
  GetMapValue,
  isIncompleteSSOSave
} from "../AuthorizationUtils";
import { UpdateSSOConfigs } from "@/Utils/Promises";
import { appstr } from "@coreapp/utils/appstr";
import { showAttachAttributesToggle } from "@coreapp/whitelabel/brand";
import { getFeatureAccess } from "@/Utils/Misc";
import { enumFeaturesList } from "@coreapp/constants";

export const SSOConfigModal = ({
  show,
  existingConfig,
  ssoMapping,
  groups,
  localMetadata,
  onClose,
  newConfig,
  setDoReload,
  reloadFiles
}) => {
  const [editingConfig, setEditingConfig] = useState(null);
  const [metadataType, setMetadataType] = useState(0);
  const [groupMapping, setGroupMapping] = useState([]);

  const wsmgr = GetWSMgr();
  const orgName = wsmgr.GetAccountName();

  useEffect(() => {
    if (existingConfig) {
      setEditingConfig(ssoMapping[existingConfig]);
      const type =
        Object.keys(metaDataTypes).find(
          key => metaDataTypes[key] === ssoMapping[existingConfig]?.type
        ) || 0;
      setMetadataType(parseInt(type));
      if (ssoMapping[existingConfig]?.mapping?.groupMapping)
        setGroupMapping(Object.entries(ssoMapping[existingConfig].mapping.groupMapping));
    } else {
      setEditingConfig({
        entityID: "",
        location: "",
        orgID: orgName,
        type: metaDataTypes[0],
        mapping: {},
        enabled: false,
        transferAttributes: false
      });
      setMetadataType(0);
    }
  }, []);

  const SelectMetadataType = value => {
    setEditingConfig(prevState => ({ ...prevState, type: metaDataTypes[value], location: "" }));
    setMetadataType(value);
  };

  const SelectLocalMetadata = value => {
    setEditingConfig(prevState => ({ ...prevState, location: value }));
  };

  const HandleCheckboxToggle = key => {
    setEditingConfig(prevState => ({ ...prevState, [key]: !prevState[key] }));
  };

  const HandleTextInput = (key, value) => {
    setEditingConfig(prevState => ({ ...prevState, [key]: value }));
  };

  const HandleAttributeInput = (key, value) => {
    setEditingConfig(prevState => ({
      ...prevState,
      mapping: { ...prevState.mapping, [key]: value }
    }));
  };

  const HandleNameIDTypeSelect = value => {
    setEditingConfig(prevState => ({ ...prevState, nameIDType: value }));
  };

  const DownloadSPMetadata = () => {
    wsmgr.DoSPMetadata(editingConfig.entityID);
  };

  const BuildGroupMappingObject = () => {
    return groupMapping.reduce((map, [key, groupID]) => {
      map[key] = groupID;
      return map;
    }, {});
  };

  const SaveSSOConfig = async () => {
    let clonedMapping = [...ssoMapping];
    if (groupMapping) editingConfig.mapping["groupMapping"] = BuildGroupMappingObject();
    if (!newConfig) {
      clonedMapping[existingConfig] = editingConfig;
    } else {
      clonedMapping.push(editingConfig);
    }

    const doSave = async () => {
      const { success } = await UpdateSSOConfigs(clonedMapping);
      if (success) {
        onClose();
        setDoReload(true);
      }
    };

    if (isIncompleteSSOSave(ssoMapping, clonedMapping)) {
      wsmgr.CreateReactAlert(
        appstr("SSO Configuration"),
        appstr("SSO Save Warning"),
        doSave,
        () => {},
        appstr("OK"),
        appstr("Cancel")
      );
    } else {
      await doSave();
    }
  };

  const AddNewGroupMap = () => {
    setGroupMapping(prevState => [...prevState, ["", null]]);
  };

  const DeleteGroupMap = idx => {
    let groupMappingClone = [...groupMapping];
    groupMappingClone.splice(idx, 1);
    setGroupMapping(groupMappingClone);
  };

  const EditGroupMapKey = (idx, value) => {
    let groupMappingClone = [...groupMapping];
    let map = groupMappingClone[idx];
    map[0] = value;
    groupMappingClone[idx] = map;

    setGroupMapping(groupMappingClone);
  };

  const SelectMapAccessGroup = (idx, value, roleIdx = null) => {
    let groupMappingClone = [...groupMapping];

    let groupIdx = idx == "_default" ? 0 : idx;
    let map = groupMappingClone[groupIdx];
    if (roleIdx != null) map[1] = { group: "0", role: roleIdx.toString() };
    else map[1] = value;

    groupMappingClone[idx] = map;

    setGroupMapping(groupMappingClone);
  };

  const mappingObj = editingConfig?.mapping;

  const HelperText = ({ text }) => {
    return (
      <Flex direction={"row"} style={{ paddingLeft: "21px" }}>
        <Text size={"xsmall"} colorModifier={"lightgrey"}>
          {appstr(text)}
        </Text>
      </Flex>
    );
  };

  return (
    <CentralizedModalContainer show={show}>
      <Modal
        id="sso-edit-modal"
        show={show}
        centerScreen={true}
        fullScreen={false}
        customStyle={{ padding: 0, width: "640px" }}
      >
        <DialogTitle
          divider={true}
          onClose={() => onClose()}
          data-testid="account-settings-authorization-sso-modal-close-btn"
        >
          <span className={"ocn-explorer-icon-label"}>
            <Icon size={"medium"} name={"gear"} />
            {appstr("SSO Configuration")}
          </span>
        </DialogTitle>

        <DialogContent>
          <Flex direction={"column"} gap={"8px"} padding={"16px 0px 0px 0px"}>
            <Input
              key={"sso-ent-id"}
              dataTestId="account-settings-authorization-sso-entity-id-input"
              label={appstr("SSO Entity ID")}
              value={editingConfig?.entityID || ""}
              type={"text"}
              size={"medium"}
              onChange={e => HandleTextInput("entityID", e.target.value)}
            />
            <Select
              {...commonSelectProps}
              dataTestId="account-settings-authorization-sso-metadata-type-select"
              label={appstr("Metadata Type")}
              inputOnly={false}
              options={MetadataTypeOptions(SelectMetadataType)}
              value={metadataType ? metadataType : 0}
            />
            {metadataType === 0 ? (
              <Input
                key={"sso-meta-loc"}
                dataTestId="account-settings-authorization-sso-metadata-url-input"
                label={appstr("Metadata Location")}
                value={editingConfig?.location || ""}
                type={"text"}
                size={"medium"}
                onChange={e => HandleTextInput("location", e.target.value)}
              />
            ) : (
              <Flex direction={"column"} gap={"4px"}>
                <Flex direction={"row"} gap={"8px"} alignItems={"flex-end"}>
                  <div style={{ width: "74%" }}>
                    <Select
                      {...commonSelectProps}
                      dataTestId="account-settings-authorization-sso-metadata-file-select"
                      label={appstr("Metadata Location")}
                      inputOnly={false}
                      options={BuildLocalMetadataOptions(
                        localMetadata,
                        SelectLocalMetadata,
                        "account-settings-authorization-sso-metadata-file"
                      )}
                      value={editingConfig?.location || ""}
                    />
                  </div>
                  <Button
                    onClick={() => {
                      UploadToSystemFolder();
                      setDoReload(true);
                    }}
                    iconProps={{ name: "cloud-arrow-up" }}
                    className={cx("oc-special-secondary", "oc-special-secondary--medium")}
                    modifier={"secondary"}
                    dataTestId="account-settings-authorization-sso-upload-metadata-btn"
                  >
                    {appstr("iwizselectupload")}
                  </Button>
                </Flex>
              </Flex>
            )}
            <Select
              {...commonSelectProps}
              dataTestId="account-settings-authorization-sso-nameid-select"
              label={appstr("NameID Handling")}
              inputOnly={false}
              options={NameIDTypeOptions(HandleNameIDTypeSelect)}
              value={editingConfig?.nameIDType || false}
            />
          </Flex>
        </DialogContent>

        <DialogContent>
          <Flex direction={"column"} gap={"8px"}>
            <Flex direction={"row"} justifyContent={"space-between"}>
              <Text modifier={"bold"}>{appstr("SAML Attributes Handling")}</Text>
            </Flex>
            <Input
              key={"sso-name-attr"}
              dataTestId="account-settings-authorization-sso-saml-name-input"
              label={appstr("SAML Name Attribute")}
              value={mappingObj?.attributeName || ""}
              type={"text"}
              size={"medium"}
              onChange={e => HandleAttributeInput("attributeName", e.target.value)}
            />
            <Input
              key={"sso-email-attr"}
              dataTestId="account-settings-authorization-sso-saml-email-input"
              label={appstr("SAML Email Attribute")}
              value={mappingObj?.attributeEmail || ""}
              type={"text"}
              size={"medium"}
              onChange={e => HandleAttributeInput("attributeEmail", e.target.value)}
            />
            <Input
              key={"sso-grp-attr"}
              dataTestId="account-settings-authorization-sso-saml-group-input"
              label={appstr("SAML Group Attribute")}
              value={mappingObj?.attributeGroup || ""}
              type={"text"}
              size={"medium"}
              onChange={e => HandleAttributeInput("attributeGroup", e.target.value)}
            />
            {editingConfig?.nameIDType ? (
              <Input
                key={"sso-id-attr"}
                dataTestId="account-settings-authorization-sso-saml-nameid-input"
                label={appstr("SAML NameID Attribute")}
                value={mappingObj?.attributeNameID || ""}
                type={"text"}
                size={"medium"}
                onChange={e => HandleAttributeInput("attributeNameID", e.target.value)}
              />
            ) : null}
            <Flex width={"50%"} padding={"0px 0px 8px"}>
              <Button
                modifier={"secondary"}
                className={"oc-special-secondary"}
                iconProps={{ name: "plus" }}
                onClick={() => AddNewGroupMap()}
                disabled={!getFeatureAccess(enumFeaturesList.SAMLGroupMapping)}
                dataTestId="account-settings-authorization-sso-add-mapping-btn"
              >
                {appstr("Add group mapping")}
              </Button>
            </Flex>
            {groupMapping &&
              groupMapping.map((item, index) => {
                return (
                  <Flex direction={"row"} gap={"20px"}>
                    <div style={{ width: "50%" }}>
                      <Input
                        key={`grpmap-${index}-${item[1]}`}
                        dataTestId="account-settings-authorization-sso-mapping-from-input"
                        label={appstr("SAML Attribute From")}
                        type={"text"}
                        size={"medium"}
                        value={item[0]}
                        onChange={e => {
                          EditGroupMapKey(index, e.target.value);
                        }}
                      />
                    </div>
                    <Flex direction={"row"} gap={"8px"} alignItems={"center"}>
                      <Select
                        {...commonSelectProps}
                        dataTestId="account-settings-authorization-sso-mapping-to-select"
                        label={appstr("SAML Attribute To")}
                        inputOnly={false}
                        options={BuildAccessGroupOptions(
                          groups.groups,
                          SelectMapAccessGroup,
                          index,
                          true,
                          "account-settings-authorization-sso-mapping-to"
                        )}
                        value={GetMapValue(item[1])}
                      />
                      <Flex padding={"8px 0px 0px"}>
                        <Button
                          size={"small"}
                          modifier="tertiary"
                          color="error"
                          iconOnly={true}
                          iconProps={{ name: "trash" }}
                          onClick={() => DeleteGroupMap(index)}
                          dataTestId="account-settings-authorization-sso-mapping-remove-btn"
                        />
                      </Flex>
                    </Flex>
                  </Flex>
                );
              })}
          </Flex>
        </DialogContent>

        <DialogContent>
          <Flex direction={"column"} gap={"8px"}>
            <Flex direction={"column"} gap={"4px"}>
              <Checkbox
                key={"sso-chkbx-1"}
                id={"sso-chkbx-1"}
                data-testid="account-settings-authorization-sso-enabled-checkbox"
                size="medium"
                label={appstr("SSO Enabled")}
                checked={editingConfig?.enabled}
                onToggle={() => HandleCheckboxToggle("enabled")}
              />
              <HelperText text={"ssoenableinfotext"} />
            </Flex>
            <Flex direction={"column"} gap={"4px"}>
              <Checkbox
                key={"sso-chkbx-2"}
                id={"sso-chkbx-2"}
                data-testid="account-settings-authorization-sso-auto-provision-checkbox"
                size="medium"
                label={appstr("Auto Provision")}
                checked={editingConfig?.autoProvision}
                onToggle={() => HandleCheckboxToggle("autoProvision")}
              />
              <HelperText text={"ssoautoproinfotext"} />
            </Flex>
            <Flex direction={"column"} gap={"4px"}>
              <Checkbox
                key={"sso-chkbx-3"}
                id={"sso-chkbx-3"}
                data-testid="account-settings-authorization-sso-slo-checkbox"
                size="medium"
                label={appstr("Single Logout")}
                disabled={!getFeatureAccess(enumFeaturesList.SLO)}
                checked={editingConfig?.SLOEnabled}
                onToggle={() => HandleCheckboxToggle("SLOEnabled")}
              />
              <HelperText text={"sloinfotext"} />
            </Flex>
            {!showAttachAttributesToggle || (
              <Flex direction={"column"} gap={"4px"}>
                <Checkbox
                  key={"sso-chkbx-4"}
                  id={"sso-chkbx-4"}
                  data-testid="account-settings-authorization-sso-transfer-attrs-checkbox"
                  size="medium"
                  label={appstr("transferAttributeLabel")}
                  checked={editingConfig?.transferAttributes}
                  onToggle={() => HandleCheckboxToggle("transferAttributes")}
                />
                <HelperText text={"transferAttributesInfoText"} />
              </Flex>
            )}
            {editingConfig?.SLOEnabled && (
              <Input
                key={"sso-cust-ep"}
                dataTestId="account-settings-authorization-sso-slo-url-input"
                label={`${appstr("Single Logout")} URL`}
                value={editingConfig?.hardcodedSLO || ""}
                type={"text"}
                size={"medium"}
                onChange={e => HandleTextInput("hardcodedSLO", e.target.value)}
              />
            )}
          </Flex>
        </DialogContent>
        <Divider />
        <Flex direction={"row"} justifyContent={"space-between"}>
          <div style={{ padding: "8px" }}>
            <Button
              modifier={"secondary"}
              onClick={DownloadSPMetadata}
              disabled={!editingConfig?.entityID}
              dataTestId="account-settings-authorization-sso-generate-sp-metadata-btn"
            >
              {appstr("generateSPMetadata")}
            </Button>
          </div>
          <DialogActions>
            <DialogActions.Secondary
              onClick={() => {
                let ssoURL = `/saml/sso_endpoint?entityID=${editingConfig?.entityID}`;
                window.open(ssoURL);
              }}
              disabled={!editingConfig?.entityID || !existingConfig}
              dataTestId="account-settings-authorization-sso-test-btn"
            >
              {appstr("testlink")}
            </DialogActions.Secondary>
            <DialogActions.Primary
              onClick={() => SaveSSOConfig()}
              disabled={!editingConfig?.entityID}
              dataTestId="account-settings-authorization-sso-save-btn"
            >
              {appstr("Save")}
            </DialogActions.Primary>
          </DialogActions>
        </Flex>
      </Modal>
    </CentralizedModalContainer>
  );
};
